/**
 * Wishlist — いとをかし お気に入り機能
 * localStorage ベース、Web Components + pub/sub パターン
 */

const WISHLIST_KEY = 'ts_wishlist';
const WISHLIST_TRACKING_URL = '';

function trackWishlistEvent(action, handle) {
  try {
    const payload = JSON.stringify({
      action,
      handle,
      ts: Date.now(),
      ua: navigator.userAgent,
      ref: location.pathname,
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'text/plain' });
      if (navigator.sendBeacon(WISHLIST_TRACKING_URL, blob)) return;
    }
    fetch(WISHLIST_TRACKING_URL, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'text/plain' },
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch { /* トラッキング失敗はストア動作に影響しない */ }
}

/* ── 静的クラス: Wishlist ストア ── */
class Wishlist {
  static getAll() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch {
      return [];
    }
  }

  static _save(handles) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    publish(PUB_SUB_EVENTS.wishlistUpdate, { handles });
  }

  static has(handle) {
    return this.getAll().includes(handle);
  }

  static add(handle) {
    const list = this.getAll();
    if (!list.includes(handle)) {
      list.push(handle);
      this._save(list);
      trackWishlistEvent('add', handle);
    }
  }

  static remove(handle) {
    const list = this.getAll().filter(h => h !== handle);
    this._save(list);
    trackWishlistEvent('remove', handle);
  }

  static toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  }

  static count() {
    return this.getAll().length;
  }
}

/* ── <wishlist-button> — ハートトグルボタン ── */
class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.handle = null;
  }

  connectedCallback() {
    this.handle = this.dataset.handle;
    if (!this.handle) return;

    this.button = this.querySelector('.wishlist-btn');
    if (!this.button) return;

    this._syncState();
    this.button.addEventListener('click', this._onClick.bind(this));
    this._unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, this._syncState.bind(this));
  }

  disconnectedCallback() {
    if (this._unsubscribe) this._unsubscribe();
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const added = Wishlist.toggle(this.handle);
    this._updateLabel(added);
  }

  _syncState() {
    const active = Wishlist.has(this.handle);
    this.button.classList.toggle('is-active', active);
    this._updateLabel(active);
  }

  _updateLabel(active) {
    const label = this.button.querySelector('.wishlist-btn__label');
    if (label) {
      label.textContent = active ? 'お気に入り済み' : 'お気に入りに追加';
    }
    this.button.setAttribute('aria-label', active ? 'お気に入りから削除' : 'お気に入りに追加');
  }
}

customElements.define('wishlist-button', WishlistButton);

/* ── <wishlist-count> — ヘッダーバッジ ── */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this._update();
    this._unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, this._update.bind(this));
  }

  disconnectedCallback() {
    if (this._unsubscribe) this._unsubscribe();
  }

  _update() {
    const count = Wishlist.count();
    this.style.display = count > 0 ? '' : 'none';
    const span = this.querySelector('span');
    if (span) span.textContent = count;
  }
}

customElements.define('wishlist-count', WishlistCount);

/* ── <wishlist-page> — お気に入り一覧ページ ── */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this._render();
    this._unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, this._render.bind(this));
  }

  disconnectedCallback() {
    if (this._unsubscribe) this._unsubscribe();
  }

  async _render() {
    const handles = Wishlist.getAll();

    if (handles.length === 0) {
      this.innerHTML = `
        <div class="wishlist-page__empty">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <p>お気に入りに追加された商品はありません。</p>
        </div>`;
      return;
    }

    this.innerHTML = '<div class="wishlist-page__loading">読み込み中...</div>';

    const products = await Promise.all(
      handles.map(handle =>
        fetch(`/products/${handle}.json`)
          .then(r => r.ok ? r.json() : null)
          .then(data => data ? data.product : null)
          .catch(() => null)
      )
    );

    const validProducts = products.filter(Boolean);

    if (validProducts.length === 0) {
      this.innerHTML = `
        <div class="wishlist-page__empty">
          <p>お気に入り商品の読み込みに失敗しました。</p>
        </div>`;
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'wishlist-page__grid';

    validProducts.forEach(product => {
      const img = product.images && product.images[0]
        ? product.images[0].src
        : '';
      const price = product.variants && product.variants[0]
        ? new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(product.variants[0].price)
        : '';

      const item = document.createElement('div');
      item.className = 'wishlist-page__item';
      item.innerHTML = `
        ${img ? `<div class="wishlist-page__item-image"><a href="/products/${product.handle}"><img src="${img}" alt="${product.title}" loading="lazy" width="400" height="400"></a></div>` : ''}
        <div class="wishlist-page__item-info">
          <h3 class="wishlist-page__item-title"><a href="/products/${product.handle}">${product.title}</a></h3>
          ${price ? `<p class="wishlist-page__item-price">${price}</p>` : ''}
        </div>
        <div class="wishlist-page__item-remove">
          <wishlist-button data-handle="${product.handle}">
            <button class="wishlist-btn wishlist-btn--card is-active" type="button" aria-label="お気に入りから削除">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>
          </wishlist-button>
        </div>`;
      grid.appendChild(item);
    });

    this.innerHTML = '';
    this.appendChild(grid);
  }
}

customElements.define('wishlist-page', WishlistPage);
